import React from 'react';
import { Heart, Clock, Shield, Home, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import ServicePageLayout from './ServicePageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export default function NursingCare() {
  const { t } = useTranslation();
  
  return (
    <ServicePageLayout
      title={t('services.nursingCare.title')}
      subtitle={t('services.nursingCare.subtitle')}
      icon={Heart}
      heroColor="from-rose-500 to-pink-600"
      enquiryTitle={t('services.nursingCare.title')}
    >
      {/* Main Description */}
      <Card className="mb-8 shadow-lg" data-testid="service-main-card">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('services.nursingCare.mainTitle')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            {t('services.nursingCare.mainDesc1')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('services.nursingCare.mainDesc2')}
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-247">
          <CardContent className="p-6">
            <Clock className="w-12 h-12 text-rose-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.nursingCare.features.roundClock.title')}</h3>
            <p className="text-gray-700">
              {t('services.nursingCare.features.roundClock.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-certified">
          <CardContent className="p-6">
            <Shield className="w-12 h-12 text-rose-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.nursingCare.features.certified.title')}</h3>
            <p className="text-gray-700">
              {t('services.nursingCare.features.certified.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-home">
          <CardContent className="p-6">
            <Home className="w-12 h-12 text-rose-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.nursingCare.features.homeHotel.title')}</h3>
            <p className="text-gray-700">
              {t('services.nursingCare.features.homeHotel.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-personalized">
          <CardContent className="p-6">
            <Heart className="w-12 h-12 text-rose-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.nursingCare.features.personalized.title')}</h3>
            <p className="text-gray-700">
              {t('services.nursingCare.features.personalized.desc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Services Offered */}
      <Card className="mb-8 shadow-lg" data-testid="services-offered-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.nursingCare.servicesTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t('services.nursingCare.servicesList').map((service) => (
              <div key={service} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{service}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Care Packages */}
      <Card className="mb-8 shadow-lg" data-testid="packages-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.nursingCare.packagesTitle')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-rose-50 rounded-xl p-6 border border-rose-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.nursingCare.packages.basic.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.nursingCare.packages.basic.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.nursingCare.packages.basic.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-rose-50 rounded-xl p-6 border border-rose-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.nursingCare.packages.standard.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.nursingCare.packages.standard.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.nursingCare.packages.standard.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-rose-50 rounded-xl p-6 border border-rose-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.nursingCare.packages.premium.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.nursingCare.packages.premium.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.nursingCare.packages.premium.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </ServicePageLayout>
  );
}
